import { uniqueId } from 'lodash';
import { useEffect, useRef, useState } from 'react';

interface UseScrollingHandlerProps {
    isOpen: boolean;
    contentRef: React.MutableRefObject<HTMLDivElement | undefined>;
    preserveDrawerScrollLocation: boolean;
}

interface UseScrollingHandlerReturnedValue {
    isContentScrollable: boolean;
}

/**
 * This hook encapsulates the logic of scrolling in regards to drawer component - mouse scroll wheel
 * should scroll the drawer itself, and preserving the scrolling location of the drawer between reopening the drawer
 *
 * @param {UseScrollingHandlerProps} props
 * @param {boolean} props.isOpen is the drawer open
 * @param {React.MutableRefObject<HTMLDivElement | undefined>} props.contentRef reference to the drawer's content element
 * @param {boolean} props.preserveDrawerScrollLocation should preserve the drawer's scrolling location when reopening it
 * @return {UseScrollingHandlerReturnedValue} Returns `isContentScrollable` flag to indicate if the content is scrollable
 */
export const useScrollingHandler = ({
    isOpen,
    contentRef,
    preserveDrawerScrollLocation,
}: UseScrollingHandlerProps): UseScrollingHandlerReturnedValue => {
    const idRef = useRef(uniqueId());
    const id = idRef.current;
    const [isContentScrollable, setIsContentScrollable] = useState(false);

    useEffect(() => {
        document.body.classList.toggle(`fvrr-drawer-opened-${id}`, isOpen);

        return () => {
            document.body.classList.toggle(`fvrr-drawer-opened-${id}`, false);
        };
    }, [isOpen, id]);

    useEffect(() => {
        if (!preserveDrawerScrollLocation && !isOpen && contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [preserveDrawerScrollLocation, contentRef, isOpen]);

    useEffect(() => {
        setTimeout(() => {
            if (!isOpen) {
                return;
            }

            if (contentRef.current && contentRef.current.scrollHeight > contentRef.current.clientHeight) {
                setIsContentScrollable(true);
            }
        });
    }, [contentRef, isOpen]);

    return {
        isContentScrollable,
    };
};
